<template>
  <!-- With the book's own section: "Bläddra i boken" goes there, then the purchase. -->
  <highlight-card
    :button="{ label: bookPanel.section ? texts.button : texts.title }"
    :heading="sprintf(texts.heading, deceasedName)"
    :secondary-button="bookPanel.section ? { label: texts.title } : undefined"
    :status="status"
    :text="texts.text"
    @action="onAction"
  >
    <template #media>
      <button
        class="memory-book-card__cover"
        type="button"
        :aria-label="texts.button"
        :class="{ 'memory-book-card__cover--lifestyle': lifestyle }"
        @click="onCover"
      >
        <picture v-if="lifestyle">
          <source
            :media="`(min-width: ${DESKTOP_PX}px)`"
            :sizes="DESKTOP_SIZES"
            :srcset="lifestyle.desktop"
          >
          <img
            loading="lazy"
            :alt="sprintf(texts.lifestyleAlt, deceasedName)"
            :sizes="PHONE_SIZES"
            :src="lifestyle.src"
            :srcset="lifestyle.phone"
          >
        </picture>
        <img
          loading="lazy"
          v-else
          :alt="texts.coverAlt"
          :height="book.cover.height || undefined"
          :src="book.cover.url"
          :width="book.cover.width || undefined"
        >
      </button>
    </template>
  </highlight-card>
</template>

<script lang="ts" setup>
  import { sprintf } from "sprintf-js";
  import { computed } from "vue";
  import {
    formatPrice,
    lifestyleUrl,
    lowestPrice,
    type MemoryBook,
  } from "../../../highlights/memory-book";
  import {
    bookPanel,
    browseBook,
    openBookPanel,
  } from "../../../highlights/memory-book-panel";
  import { memoryBookTexts } from "../../../highlights/texts";
  import HighlightCard from "../HighlightCard.vue";

  /**
   * The family's memory book, for sale to visitors. The button buys, as the gift's and the
   * flowers' do; the cover is the quieter way in, to look through the book.
   */
  const props = defineProps<{
    book: MemoryBook;
    deceasedName: string;
  }>();

  /** $breakpoint-m: from here the card is a row, its media 40% of it and 16:9. */
  const DESKTOP_PX = 992;
  const DESKTOP_SIZES = "(min-width: 1280px) 500px, 40vw";
  // Below it the card is a slide of the swipe track, its media 16:8.
  const PHONE_SIZES = "86vw";
  const WIDTHS = [480, 800, 1200];

  const texts = memoryBookTexts();

  /** The book in someone's hands, cropped around the book; else the card shows the flat cover. */
  const lifestyle = computed(() => {
    const url = props.book.lifestyle?.url;
    if (!url) {
      return null;
    }
    const srcset = (ratio: string, zoom: number) =>
      WIDTHS.map(
        (width) => `${lifestyleUrl(url, { ratio, width, zoom })} ${width}w`
      ).join(", ");
    return {
      desktop: srcset("16:9", 1.1),
      phone: srcset("2:1", 1.2),
      src: lifestyleUrl(url, { ratio: "2:1", width: 800, zoom: 1.2 }),
    };
  });
  function onAction(index: number) {
    if (bookPanel.section && index === 0) {
      browseBook();
    } else {
      openBookPanel("button");
    }
  }

  /** The cover browses the book in its section; without the section it opens the panel. */
  function onCover() {
    if (bookPanel.section) {
      browseBook();
    } else {
      openBookPanel("cover");
    }
  }

  // "+ frakt" when there is a delivery fee, so the price never reads as delivered.
  const status = computed(() =>
    sprintf(
      props.book.shipping ? texts.statusShipping : texts.status,
      props.book.pages,
      formatPrice(lowestPrice(props.book), props.book.currency)
    )
  );
</script>
